import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroArrowTopRightOnSquare, heroDocumentText } from '@ng-icons/heroicons/outline';
import {
  ToastrService,
  UiBackdropButton,
  UiBadge,
  UiButton,
  UiEmptyState,
  UiRecordList,
  UiRecordRow,
  UiSkeletonGrid,
} from '../../../shared/ui';
import type { BillingMeInvoice } from '../../../core/api/generated/models/BillingMeInvoice';
import type { BillingMeInvoiceItem } from '../../../core/api/generated/models/BillingMeInvoiceItem';
import { BillingApiService } from '../../billing/billing-api.service';

type Tone = 'success' | 'warning' | 'danger' | 'neutral' | 'info';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  open: 'Em aberto',
  paid: 'Paga',
  past_due: 'Vencida',
  void: 'Cancelada',
};

const STATUS_TONE: Record<string, Tone> = {
  draft: 'neutral',
  open: 'info',
  paid: 'success',
  past_due: 'warning',
  void: 'neutral',
};

interface InvoiceRow {
  id: string;
  numberLabel: string;
  statusLabel: string;
  statusTone: Tone;
  totalLabel: string;
  periodLabel: string;
  receiptUrl: string | null;
}

interface ItemRow {
  id: string;
  description: string;
  amountLabel: string;
}

function formatMoney(amount: string, currency: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return amount;
  return value.toLocaleString('pt-BR', { style: 'currency', currency });
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Seção Faturas (F3): lista as faturas da mensalidade (período/total em R$/status) com
 * detalhe de itens (carregado sob demanda) e link para o recibo do provedor. Só leitura
 * (billing:invoice:read, gated na CobrancaPage).
 */
@Component({
  selector: 'app-billing-invoices-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, UiBackdropButton, UiBadge, UiButton, UiEmptyState, UiRecordList, UiRecordRow, UiSkeletonGrid],
  providers: [provideIcons({ heroArrowTopRightOnSquare, heroDocumentText })],
  templateUrl: './invoices-section.component.html',
})
export class InvoicesSectionComponent {
  private readonly api = inject(BillingApiService);
  private readonly toastr = inject(ToastrService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  private readonly invoices = signal<BillingMeInvoice[]>([]);
  readonly expandedId = signal<string | null>(null);
  private readonly itemsById = signal<Record<string, ItemRow[]>>({});
  readonly loadingItems = signal(false);

  readonly rows = computed<InvoiceRow[]>(() => this.invoices().map((inv) => this.toRow(inv)));
  readonly expandedItems = computed<ItemRow[] | null>(() => {
    const id = this.expandedId();
    return id ? (this.itemsById()[id] ?? null) : null;
  });

  constructor() {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api
      .getInvoices({ page: 1, pageSize: 50 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.invoices.set(res.invoices);
          this.loading.set(false);
        },
        error: () => {
          this.error.set('Não foi possível carregar as faturas. Tente novamente.');
          this.loading.set(false);
        },
      });
  }

  toggle(id: string): void {
    if (this.expandedId() === id) {
      this.expandedId.set(null);
      return;
    }
    this.expandedId.set(id);
    if (this.itemsById()[id]) return; // já carregado
    this.loadingItems.set(true);
    this.api
      .getInvoice(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          const items = detail.items.map((it) => this.toItemRow(it, detail.invoice.currency));
          this.itemsById.update((m) => ({ ...m, [id]: items }));
          this.loadingItems.set(false);
        },
        error: () => {
          this.loadingItems.set(false);
          this.expandedId.set(null);
          this.toastr.error('Não foi possível carregar os itens da fatura', 'Tente novamente.');
        },
      });
  }

  private toRow(inv: BillingMeInvoice): InvoiceRow {
    const start = formatDate(inv.period_start);
    const end = formatDate(inv.period_end);
    return {
      id: inv.id,
      numberLabel: inv.invoice_number ?? 'Fatura',
      statusLabel: STATUS_LABEL[inv.status] ?? inv.status,
      statusTone: STATUS_TONE[inv.status] ?? 'neutral',
      totalLabel: formatMoney(inv.total, inv.currency),
      periodLabel: start && end ? `${start} – ${end}` : (start ?? end ?? ''),
      receiptUrl: inv.provider_invoice_url,
    };
  }

  private toItemRow(item: BillingMeInvoiceItem, currency: string): ItemRow {
    return {
      id: item.id,
      description: item.description,
      amountLabel: formatMoney(item.amount, currency),
    };
  }
}
